/**
 * Next.js App 入口文件
 * Build trigger: 2025-12-08 - Force regenerate all chunks
 */
import React, { useEffect, useState } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { Router, useRouter } from 'next/router';
import { Inter } from 'next/font/google';
import NProgress from 'nprogress';
import '@/styles/mixins.less';
import '@/styles/fonts.css';
import '@/styles/globals.less';
import 'nprogress/nprogress.css';
import { App as AntdApp } from 'antd';
import ConfigProvider from '@/context/ConfigProvider';
import withTheme from '@/theme';
import '@/lib/hooks/common/firebaseConfig/init';
import { appWithTranslation } from 'next-i18next';
import { Locale } from 'antd/lib/locale';
import { CurrentLanguage } from '@/shared/language';
import UserProvider from '@/context/UserProvider';
import dynamic from 'next/dynamic';
import '../styles/tailwind.css'; // 先导入 Tailwind
import '../styles/globals.less';
import 'maplibre-gl/dist/maplibre-gl.css'; // 确保 MapLibre 的样式生效
import { useWindowSize } from 'react-use';
import GlobalLoading from '@/components/Common/GlobalLoading';
import { isProd } from '@/shared/app-common';
import Login from '@/components/Login';
import ErrorBoundary from '@/components/Common/ErrorBoundary';
import { NextIntlClientProvider } from 'next-intl';
import useClarity from '@/lib/hooks/common/firebaseConfig/useClarityInit';

// 配置字体
const inter = Inter({
  subsets: ['latin'], // 指定需要的字符子集
  weight: ['400', '500', '600', '700'], // 指定需要的字体权重
  display: 'swap', // 优化字体显示的加载方式
  variable: '--font-inter', // 添加CSS变量支持
});

require('../styles/ins-theme.less');

const CookieConsentContent = dynamic(() => import('@/components/Common/CookieConsent'), {
  ssr: false,
});
const ArticleBottomAlert = dynamic(() => import('@/components/Common/ArticleBottomAlert'), {
  ssr: false,
});
const CouponButton = dynamic(() => import('@/components/Common/coupon/CouponButton'), {
  ssr: false,
});

export type NextPageWithLayout<P = {}> = NextPage<P> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout<
  P = {
    dehydratedState?: unknown;
  },
> = AppProps<P> & {
  Component: NextPageWithLayout<P>;
};

// 仅在开发环境屏蔽 findDOMNode 的警告  React 19 会删除findDOMNode Api
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === 'string' &&
      (args[0].includes('findDOMNode is deprecated') ||
        (args[0].includes('%s is deprecated in StrictMode') && args[1] === 'findDOMNode'))
    ) {
      return; // 直接忽略
    }
    originalError(...args);
  };
}

function App({ Component, pageProps }: AppPropsWithLayout) {
  const [loc, setLoc] = useState<Locale>(CurrentLanguage('en').locale);
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const router = useRouter();
  const { width } = useWindowSize();
  useClarity();

  useEffect(() => {
    setLoc(CurrentLanguage(router.locale).locale);
  }, [router.locale]);

  useEffect(() => {
    // 优化 vconsole 时机
    // @ts-ignore
    if (typeof window !== 'undefined' && !isProd) {
      if (!/android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || navigator.vendor)) return;
      const hasVconsole = document.getElementById('vconsole-script');
      if (hasVconsole) return;
      const scrpt = document.createElement('script');
      scrpt.src = 'https://unpkg.com/vconsole/dist/vconsole.min.js';
      scrpt.id = 'vconsole-script';
      document.body.appendChild(scrpt);
      scrpt.onload = () => {
        // @ts-ignore
        window.VConsole = new window.VConsole();
        console.log('vConsole is enabled');
      };
    }
  }, []);

  useEffect(() => {
    const handleStart = () => NProgress.start();
    const handleStop = () => NProgress.done();

    Router.events.on('routeChangeStart', handleStart);
    Router.events.on('routeChangeError', handleStop);
    Router.events.on('routeChangeComplete', handleStop);

    return () => {
      Router.events.off('routeChangeStart', handleStart);
      Router.events.off('routeChangeError', handleStop);
      Router.events.off('routeChangeComplete', handleStop);
    };
  }, []);

  return withTheme(
    <ErrorBoundary>
      <div className={`${inter.className} ${inter.variable}`}>
        <AntdApp>
          <NextIntlClientProvider locale={router.locale || 'en'} messages={{}}>
            <ConfigProvider>
              <UserProvider>
                {getLayout(<Component {...pageProps} />)}

                <Login />
                <GlobalLoading />
                <ArticleBottomAlert />
                <CookieConsentContent />
                {/* <CouponButton /> */}
              </UserProvider>
            </ConfigProvider>
          </NextIntlClientProvider>
        </AntdApp>
      </div>
    </ErrorBoundary>,
    loc
  );
}

export default appWithTranslation(App);
